<template>
  <div class="row">
    <div class="col">
      <!-- Location Name -->
      <div class="row">
        <div class="col-4">
          <z-input v-model="firmName" name="firmName" label="Name" :validator="v$" @keyup="changed(true)" @change="changed(true)" />
        </div>
        <div v-if="isSuperAdmin && isCustomerId" class="col pt-4">
          <span>Customer Id:</span>
          <span class="ms-2">{{ customerId }}</span>
          <a href="#" class="ms-2" @click="copy(customerId)">
            <FontAwesomeIcon icon="fas fa-clipboard" />
          </a>
        </div>
        <div v-else class="col"></div>
        <div class="col-2">
          <z-select v-model="firmType" name="firmType" label="Type" :options="types" :disabled="!isSuperAdmin" @change="changed(true)" />
        </div>
        <div class="col-2">
          <input
            v-model="isInactive"
            type="checkbox"
            true-value="true"
            false-value="false"
            class="custom-checkbox me-2 custom-inactive-check"
            :disabled="!isSuperAdmin"
            @click="changed(true)"
          />
          <span class="custom-inactive">
            Inactive?
            <div
              ref="info"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="This setting this will also apply to any child locations. Users will also become inactive."
            >
              <i class="fas fa-info-circle"></i>
            </div>
          </span>
        </div>
      </div>
      <!-- Additional ID references -->
      <div class="row custom-border-top">
        <div class="col-2">
          <z-input v-model="firmId" name="firmId" label="Id" @keyup="changed(false)" @change="changed(false)" />
        </div>
        <div class="col-2">
          <z-input v-model="firmNumber" name="firmNumber" label="Number" @keyup="changed(false)" @change="changed(false)" />
        </div>
        <div v-if="isLicensed" class="col-2 pt-4">
          <span>Under License</span>
        </div>
        <div v-else-if="isProduct" class="col-2">
          <div class="row">
            <div class="col">
              <z-select v-model="product" name="product" label="Product" :options="products" :disabled="isCancelled" @change="subscriptionChange" />
            </div>
          </div>
          <div v-if="isShop" class="row">
            <div class="col">
              <span v-if="isCancelled" class="custom-cancelled">Cancelled</span>
              <button v-else class="btn btn-primary btn-sm" @click="subscriptionCancel">Cancel Subscription</button>
            </div>
          </div>
        </div>
        <div v-if="isSuperAdmin" class="col-2">
          <input
            v-model="isLicensed"
            type="checkbox"
            true-value="true"
            false-value="false"
            class="custom-checkbox me-2 custom-inactive-check"
            @click="changed(true)"
          />
          <span class="custom-inactive">Under License?</span>
        </div>
        <div v-else class="col-2"></div>
      </div>
      <!-- Type and IsInactive? -->
      <div class="row">
        <div class="col"></div>
      </div>
      <div class="row">
        <div class="col-3">
          <!-- Lookup for User Contact for this Location -->
          <z-search
            v-model.trim="searchTerm"
            name="search"
            class="custom-search-user"
            label="Main Contact:"
            placeholder="Search"
            :disabled="isLoading"
          />
          <div v-if="isFoundUsers" class="custom-found-user">
            <ul>
              <li v-for="user of foundFirmUsers" :key="user.user" :value="user.user" @click="selectUser(user)">
                {{ formatName(user) }}
              </li>
            </ul>
          </div>
        </div>
        <div class="col">
          <span class="custom-contact">{{ mainContact }}</span>
          <span v-if="isTempPassword" class="custom-contact ms-5">Temp Password:</span>
          <span class="custom-contact ms-2">{{ tempPassword }}</span>
        </div>
      </div>
    </div>
    <div v-if="isSuperAdmin" class="row">
      <div class="col">
        <div v-if="isOkayToMove" class="row mt-1">
          <div class="col">
            <div class="row custom-border-top">
              <div class="col">
                <p>To move this location to another, search and select the location (to move to) and click the 'Move' button.</p>
              </div>
            </div>
            <div class="row">
              <div class="col-4">
                <Multiselect
                  ref="searchLocation"
                  v-model="foundLocation"
                  class="form-control custom-multiselect"
                  placeholder="type to search"
                  :close-on-select="true"
                  :filter-results="false"
                  :min-chars="1"
                  :resolve-on-load="false"
                  :delay="500"
                  :searchable="true"
                  :open-direction="'top'"
                  :options="
                    async (query) => {
                      return await fetchLocations(query)
                    }
                  "
                  label="firmName"
                  value-prop="firm"
                  @select="selectedLocation"
                />
              </div>
              <div class="col-1">
                <button class="btn btn-primary btn-sm" @click="reassignLocation">Move</button>
              </div>
              <div class="col"></div>
            </div>
            <div class="row">
              <div class="col">
                <!-- <p><b>Note:</b> Be sure to choose a "parent" location that is identical to this location's current parent.</p> -->
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <ConfirmDialog
    v-model="actionConfirm"
    :confirm-title="confirmDialogTitle"
    :confirm-action="confirmDialogAction"
    :confirm-question="confirmDialogQuestion"
    @closing="closeModal"
    @proceed="performConfirmation"
  />
</template>
<script>
import { mapState, mapActions } from 'pinia'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { authStore } from '@/stores/auth'
import { locationStore } from '@/stores/location'
import { lookupStore } from '@/stores/lookup'
import { onboardStore } from '@/stores/onboard'
import { Tooltip } from 'bootstrap'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import ZSearch from '@/components/utilities/elements/Search.vue'
import ZInput from '@/components/utilities/elements/Input.vue'
import ZSelect from '@/components/utilities/elements/Select.vue'
import ConfirmDialog from '@/components/utilities/elements/ConfirmDialog.vue'
import Multiselect from '@vueform/multiselect'
export default {
  name: 'LocationBasic',
  components: {
    ZSearch,
    ZInput,
    ZSelect,
    Multiselect,
    ConfirmDialog,
    FontAwesomeIcon
  },
  props: {
    parent: Object,
    isAdd: {
      type: Boolean,
      default: false
    }
  },
  emits: ['message', 'changed', 'saved', 'moved'],
  data() {
    return {
      foundLocation: null,
      isPushing: false,
      rules: {
        firmName: { required },
        firmType: { required }
      },
      v$: null,
      previousProduct: null,
      confirmTask: null,
      actionConfirm: null,
      confirmDialogTitle: 'Confirm Subscription Cancellation',
      confirmDialogAction: 'Cancel Subscription',
      confirmDialogQuestion: 'Are you sure you wish to cancel this subscription?'
    }
  },
  beforeMount() {
    this.v$ = useVuelidate(this.rules, this.location)
    this.isPushing = false
    this.newUsername = ''
    this.editingUsername = false
  },
  mounted() {
    new Tooltip(this.$refs.info)
    this.init()
  },
  computed: {
    ...mapState(authStore, ['profile']),
    ...mapState(lookupStore, ['lookups']),
    ...mapState(locationStore, ['loading', 'location', 'userSearchTerm', 'foundFirmUsers', 'foundLocations']),
    ...mapState(onboardStore, ['products']),
    isSuperAdmin() {
      let _is = false
      if ([1].includes(this.profile.roles[0])) {
        _is = true
      }
      return _is
    },
    isAdmin() {
      let _is = false
      if ([2, 3, 4, 5, 7, 9, 11].includes(this.profile.roles[0])) {
        _is = true
      }
      return _is
    },
    isShop() {
      let _is = false
      if (this.location) {
        if (this.location.firmType === 7) {
          _is = true
        }
      }
      return _is
    },
    isLoading() {
      let _is = false
      if (this.loading) {
        _is = this.loading.users || this.loading.locations
      }
      return _is
    },
    isCancelled() {
      let _is = false
      if (this.location) {
        _is = this.location.isCancelled
      }
      return _is
    },
    isTempPassword() {
      let _is = false
      if (this.location) {
        if (this.location.tempPassword !== null) {
          _is = true
        }
      }
      return _is
    },
    types() {
      let _types = []
      if (this.lookups) {
        this.lookups['FirmType'].forEach((element) => {
          if (element.value >= this.parentType + 1) {
            _types.push(element)
          }
        })
      }
      console.log('Firm Types', _types)
      return _types
    },
    parentType() {
      let _type = null
      if (this.location) {
        _type = this.location.parentType
      }
      return _type
    },
    isOkayToMove() {
      let _is = false
      if (this.location) {
        if (this.location.firmType > 3) {
          _is = true
        }
      }
      return _is
    },
    firmName: {
      get() {
        let _data = null
        if (this.location) {
          _data = this.location.firmName
        }
        return _data
      },
      set(value) {
        this.setLocation({
          name: 'firmName',
          value: value
        })
      }
    },
    firmType: {
      get() {
        let _data = null
        if (this.location) {
          _data = this.location.firmType
        }
        return _data
      },
      set(value) {
        this.setLocation({
          name: 'firmType',
          value: parseInt(value)
        })
      }
    },
    customerId() {
      let _data = null
      if (this.location) {
        _data = this.location.customerId
      }
      return _data
    },
    isCustomerId() {
      let _is = false
      if (this.location) {
        if (this.location.customerId !== null) {
          _is = true
        }
      }
      return _is
    },
    product: {
      get() {
        let _data = null
        if (this.location) {
          _data = this.location.product
        }
        return _data
      },
      set(value) {
        this.setLocation({
          name: 'product',
          value: value
        })
      }
    },
    isProduct() {
      let _is = false
      if (this.location) {
        if (this.location.product !== null) {
          _is = true
        }
      }
      return _is
    },
    postalCode: {
      get() {
        let _data = null
        if (this.location) {
          _data = this.location.postalCode
        }
        return _data
      },
      set(value) {
        this.setLocation({
          name: 'postalCode',
          value: value
        })
      }
    },
    isInactive: {
      get() {
        let _data = false
        if (this.location) {
          _data = this.location.isInactive
        }
        return _data
      },
      set(value) {
        this.setLocation({
          name: 'isInactive',
          value: value === 'true'
        })
      }
    },
    isLicensed: {
      get() {
        let _data = false
        if (this.location) {
          _data = this.location.isLicensed
        }
        return _data
      },
      set(value) {
        this.setLocation({
          name: 'isLicensed',
          value: value === 'true'
        })
      }
    },
    firmId: {
      get() {
        let _data = null
        if (this.location) {
          _data = this.location.firmId
        }
        return _data
      },
      set(value) {
        this.setLocation({
          name: 'firmId',
          value: value
        })
      }
    },
    firmNumber: {
      get() {
        let _data = null
        if (this.location) {
          _data = this.location.firmNumber
        }
        return _data
      },
      set(value) {
        this.setLocation({
          name: 'firmNumber',
          value: value
        })
      }
    },
    mainContact() {
      let _data = null
      if (this.location) {
        _data = this.location.mainContact
      }
      return _data
    },
    tempPassword() {
      let _data = null
      if (this.location) {
        _data = this.location.tempPassword
      }
      return _data
    },
    searchTerm: {
      get() {
        return this.userSearchTerm
      },
      set(value) {
        this.setUserSearchTerm(value)
        this.searchForUsers()
      }
    },
    isFoundUsers() {
      let _is = false
      if (this.foundFirmUsers) {
        if (this.foundFirmUsers.length > 0) {
          _is = true
        }
      }
      return _is
    }
  },
  methods: {
    ...mapActions(locationStore, [
      'setLocation',
      'saveLocation',
      'setUserSearchTerm',
      'searchFirmUser',
      'pushLocation',
      'searchLocations',
      'resetFoundLocations',
      'moveLocation'
    ]),
    ...mapActions(onboardStore, ['changeSubscription', 'cancelSubscription']),
    init() {
      setTimeout(() => {
        this.previousProduct = this.product
      }, 250)
    },
    closeModal() {
      if (this.confirmTask === 'changeSubscription') {
        this.setLocation({
          name: 'product',
          value: this.previousProduct
        })
      }
      this.actionConfirm = null
    },
    changed(is) {
      this.$emit('changed', is)
    },
    copy(value) {
      console.log('Clipboard Copy', value)
      navigator.clipboard
        .writeText(value)
        .then(() => {
          alert('Copied to clipboard')
        })
        .catch((err) => console.error('Clipboard copy failed:', err))
    },
    async searchForUsers() {
      console.log('Search Users...', this.parent)
      let data = {
        firm: this.parent.type === 'Corporate' ? this.parent.firm : this.parent.parent,
        search: this.userSearchTerm
      }
      await this.searchFirmUser(data)
    },
    formatName(user) {
      let data = null
      if (user.firstname) {
        data = `${user.lastname}, ${user.firstname}`
      } else {
        data = user.lastname
      }
      return data
    },
    selectUser(user) {
      let data = null
      console.log('Selected User', user)
      if (user.firstname) {
        data = `${user.lastname}, ${user.firstname}`
      } else {
        data = user.lastname
      }
      this.setLocation({
        name: 'mainContactId',
        value: parseInt(user.user)
      })
      this.setLocation({
        name: 'mainContact',
        value: data
      })
      this.setUserSearchTerm(data)
      this.changed()
    },
    async fetchLocations(query) {
      let search = {
        search: query,
        type: null,
        firmType: this.firmType,
        firm: this.location.firm
      }
      return await this.searchLocations(search)
    },
    selectedLocation() {
      this.$nextTick(() => {
        let found = this.foundLocations.find((x) => parseInt(x.firm) === parseInt(this.foundLocation))
        if (found) {
          console.log('Found Location', found)
        }
      })
    },
    async reassignLocation() {
      let data = {
        firm: this.location.firm,
        firm2: this.foundLocation
      }
      console.log('Moving Location', data)
      this.resetFoundLocations()
      this.$refs.searchLocation.clear()
      this.$refs.searchLocation.clearSearch()
      this.foundLocation = null
      let result = await this.moveLocation(data)
      console.log('Move Result', result)
      if (result !== undefined && result !== null) {
        if (parseInt(result) > 0) {
          // refresh
          this.$emit('moved', data)
        } else {
          alert('Move failed, ensure the Location can be a valid "child" under the targeted move location.')
        }
      }
    },
    subscriptionChange() {
      if (this.isCancelled) {
        this.$emit('message', 'Subscription is cancelled. Please contact support to change your subscription.')
        return
      }
      this.confirmTask = 'changeSubscription'
      this.confirmDialogTitle = 'Confirm Subscription Change'
      this.confirmDialogAction = 'CHANGE Subscription'
      this.confirmDialogQuestion = `Are you sure you wish to CHANGE this subscription to ${this.product}?`
      this.actionConfirm = true
    },
    subscriptionCancel() {
      this.confirmTask = 'cancelSubscription'
      this.confirmDialogTitle = 'Confirm Subscription Cancellation'
      this.confirmDialogAction = 'CANCEL Subscription'
      this.confirmDialogQuestion = 'Are you sure you wish to CANCEL this subscription? THIS CANNOT BE UNDONE!'
      this.actionConfirm = true
    },
    async performConfirmation() {
      this.actionConfirm = null
      if (this.confirmTask === 'cancelSubscription') {
        let data = {
          firm: this.location.firm,
          testMode: false
        }
        console.log('Cancel Subscription', data)
        await this.cancelSubscription(data)
        this.setLocation({
          name: 'isCancelled',
          value: true
        })
      } else if (this.confirmTask === 'changeSubscription') {
        let data = {
          firm: this.location.firm,
          product: this.location.product
        }
        console.log('Change Subscription', data)
        await this.changeSubscription(data)
      }
      this.confirmTask = null
      this.closeModal()
    },
    async save() {
      let response = {
        valid: false,
        firm: null,
        msg: null
      }
      try {
        const valid = await this.v$.$validate()
        if (valid) {
          console.log('Save Location', this.location)
          let result = await this.saveLocation()
          response.valid = true
          response.firm = result
          this.$emit('saved', result)
        } else {
          let msg = 'Location Not Valid'
          response.msg = msg
          this.$emit('message', msg)
        }
      } catch (e) {
        console.error(e)
        response.msg = 'Error: Unable to save'
        this.message = response.msg
      }
      return response
    }
  }
}
</script>
<style lang="scss">
.custom-inactive {
  position: relative;
  display: inline-flex;
  div {
    color: #0a58ca;
    margin-left: 5px;
    top: -5px;
  }
}
.custom-inactive-check {
  margin-top: 30px;
}
.custom-inline {
  display: inline-flex;
}
.custom-info {
  position: absolute;
  top: 0;
  right: 20px;
  color: #0a58ca;
}
.custom-search-icon {
  position: relative;
  top: 0px;
}
.custom-border-top {
  border-top: 1px solid #dee2e6;
  padding-top: 10px;
}
.custom-search-user {
  z-index: 10;
}
.custom-found-user {
  position: relative;
  width: 90%;
  max-height: 150px;
  overflow-x: hidden;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  padding: 0;
  top: -2px;
  z-index: 5;
  ul {
    list-style: none;
    margin: 0 auto;
    padding: 8px;
    cursor: pointer;
    :hover {
      background-color: #0d6efd;
      color: #fff;
    }
    li {
      padding: 2px;
    }
  }
}
.custom-icon {
  width: 100%;
  display: inline;
}
.custom-firm-name {
  color: red;
  font-size: 0.9em;
  font-weight: 500;
  margin-bottom: 10px;
  position: relative;
  top: -10px;
}
.custom-border-top {
  border-top: 1px solid #dee2e6;
}
.custom-firm-number {
  font-size: 0.8em;
  font-weight: 600;
  position: relative;
  top: -10px;
  color: #ca0a0a;
}
.custom-contact {
  position: relative;
  top: 25px;
}
</style>
